/**
 * Which page the user was on, over time, read off the envelope `url` (#31329).
 *
 * The recorder stamps the current (already scrubbed) `location.href` on EVERY
 * envelope, rrweb events included. rrweb itself only says where it is on a Meta
 * event, and an Angular route change emits no Meta, so the envelope url is the
 * only per-event page signal a single-page app gives us. The parser used to drop
 * it for rrweb events; this keeps it, compactly.
 *
 * Only CHANGES are kept (same idea as the server's `RynReplaySessionPage` trail):
 * stamping every event costs the recorder nothing, but holding one entry per
 * event here would double the memory of a long session for no extra answer.
 */
export interface IRynReplayPageTrailEntry {
  /** Epoch milliseconds UTC of the first envelope seen on this url. */
  ts: number;
  url: string;
}

/** Append `url` at `ts` unless it repeats the last entry. Blank urls are ignored. */
export const recordRynReplayPageTrail = (
  trail: IRynReplayPageTrailEntry[],
  envelope: { ts?: unknown; url?: unknown } | null | undefined
): void => {
  const url = typeof envelope?.url === 'string' ? envelope.url.trim() : '';
  const ts = envelope?.ts;
  if (!url || typeof ts !== 'number' || !Number.isFinite(ts)) {
    return;
  }
  if (trail.length && trail[trail.length - 1].url === url) {
    return;
  }
  trail.push({ ts, url });
};

/** Concatenate two chunks' trails, collapsing a repeat across the seam. */
export const mergeRynReplayPageTrails = (
  left: readonly IRynReplayPageTrailEntry[],
  right: readonly IRynReplayPageTrailEntry[]
): IRynReplayPageTrailEntry[] => {
  const merged = [...left];
  for (const entry of right) {
    recordRynReplayPageTrail(merged, entry);
  }
  return merged;
};

/**
 * The url the user was on at `ts`: the latest entry at or before it.
 *
 * Does NOT assume the trail is sorted. rrweb envelopes carry rrweb's own clock
 * and devtools envelopes `Date.now()` at enqueue, so neighbours can be a few ms
 * out of order; stopping at the first later entry would drop a click.
 */
export const resolveRynReplayPageUrlAt = (
  trail: readonly IRynReplayPageTrailEntry[],
  ts: number
): string | undefined => {
  let best: IRynReplayPageTrailEntry | undefined;
  for (const entry of trail) {
    if (entry.ts <= ts && (!best || entry.ts >= best.ts)) {
      best = entry;
    }
  }
  return best?.url;
};
